import type { ReactNode } from 'react';
import type { Components } from 'react-markdown';
import { TrainingNavItem } from '../../services/trainingService';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4';

/**
 * Build a heading's anchor id the same way GitHub does, so a hand-written
 * table of contents in a manual links to the right place.
 *
 * Punctuation is dropped rather than turned into a separator, which matters for
 * a heading that ends in one: "Sending Hours (UTC)" has to become
 * "sending-hours-utc", not "...-utc-" with a trailing hyphen that matches no
 * link. Existing hyphens survive, so "two-factor" stays "two-factor".
 */
const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

/**
 * Pull the plain text out of a heading. A heading containing inline markup
 * ("... with `{childname}` in it") arrives as an array of nodes, and calling
 * String() on that yields "[object Object]", so the id has to be built by
 * walking the children instead.
 */
const headingText = (children: ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(headingText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return headingText((children as { props: { children?: ReactNode } }).props.children);
  }
  return '';
};

/**
 * Renderers for the manual's markdown.
 *
 * None of these spread the props react-markdown hands them. That is deliberate:
 * those props include `node`, the hast node, which React would forward to the
 * DOM as a stringified object attribute. The manuals are plain markdown with no
 * raw HTML, so there are no authored attributes worth preserving either. Every
 * attribute these emit is therefore set explicitly.
 */
export function buildMarkdownComponents(
  docs: TrainingNavItem[],
  onNavigateToDoc?: (docId: string) => void
): Components {
  const heading = (Tag: HeadingTag) =>
    function Heading({ children }: { children?: ReactNode }) {
      return <Tag id={slugifyHeading(headingText(children))}>{children}</Tag>;
    };

  return {
    h1: heading('h1'),
    h2: heading('h2'),
    h3: heading('h3'),
    h4: heading('h4'),

    // No syntax highlighter: the manuals are prose and contain no fenced code
    // blocks. A fenced block would still render, as a plain <pre> styled by the
    // `& pre` rule in markdownSx, just without highlighting.
    code({ className, children }) {
      return <code className={className}>{children}</code>;
    },

    a({ href, children }) {
      // In-document anchors scroll rather than navigate.
      if (href?.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById(href.substring(1));
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Update the URL hash without jumping
                window.history.pushState(null, '', href);
              }
            }}
          >
            {children}
          </a>
        );
      }

      // Cross-document links (e.g. user-guide-admin.md). The doc id is NOT the
      // filename: resolve it through the navigation the server sent, which
      // pairs each id with its file. Docs missing from that list are the ones
      // this role cannot open, so they render as plain text instead of a link
      // that would only 403.
      if (href?.endsWith('.md') && onNavigateToDoc) {
        const fileName = href.split('/').pop();
        const target = docs.find((doc) => doc.file === fileName);

        if (!target) {
          return <span>{children}</span>;
        }

        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              onNavigateToDoc(target.id);
            }}
            style={{ cursor: 'pointer' }}
          >
            {children}
          </a>
        );
      }

      return (
        <a
          href={href}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    },
  };
}
